import { Link, useLocation } from "react-router-dom"
import { useStorage } from "./Storage"
import { FaAddressBook, FaBars, FaBox, FaBriefcase, FaBuilding, FaCog, FaCreditCard, FaFileContract, FaFileInvoiceDollar, FaShippingFast, FaSignOutAlt, FaStarOfLife, FaTools, FaUser, FaUsers } from "react-icons/fa"
import { useEffect, useState } from "react"
import { Menu, MenuLink } from "../components/layout/Menu"
import { useRoleOptions } from "../pages/crud/Users"
import { Sidebar, SidebarCollapsible, SidebarLink } from "../components/layout/Sidebar"
import { Breadcrumb } from "../components/layout/Breadcrumb"
import { useBreakpoints } from "../hooks/useBreakpoints"
import { languages, useLocale, useTranslations } from "./Localization"
import { DateInput } from "../components/forms/Inputs"
import { useApi } from "../hooks/useApi"
import { useSociete } from "../hooks/useSociete"

function AuthLayout({ children }) {
	return <div className="h-screen p-6 md:p-10 bg-gray-200 flex flex-col">
		<div className="flex flex-col grow justify-center items-center">
			<div className="bg-gray-50 p-7 shadow-xl rounded-xl w-full md:w-[360px]">
				{children}
			</div>
		</div>
	</div>
}

function AdminLayout({ children }) {
	const t = useTranslations()
	const { isMobile } = useBreakpoints()
	const [{ token, date }, , set] = useStorage()
	const [locale, setLocale] = useLocale()
	const api = useApi()
	const { pathname } = useLocation()
	const societe = useSociete()

	const [sidebar, setSidebar] = useState(!isMobile)

	const logout = async () => {
		await api.post('/signout')
		set('token')(null)
	}
	const roleOptions = useRoleOptions()
	const language = languages.find(x => x.locale === locale)

	useEffect(() => {
		window.scrollTo(0, 0)
	}, [pathname])

	useEffect(() => {
		if (!date) set('date')(new Date())
	}, [])

	useEffect(() => {
		set('date')(new Date())
	}, [token])

	return <div className="h-screen flex bg-gray-50">
		<Sidebar value={sidebar} onChange={setSidebar}>
			<Link className="py-5 mx-5 border-b border-gray-50 text-center" to="/">{societe?.nom || t('Société')}</Link>
			<div className="px-5 py-5 grow flex flex-col gap-3">
				<SidebarLink to="/gerants" icon={<FaBriefcase />}>{t('Gérants')}</SidebarLink>
				<SidebarLink to="/clients" icon={<FaCreditCard />}>{t('Clients & Prospects')}</SidebarLink>
				<SidebarLink to="/chantiers" icon={<FaBuilding />}>{t('Chantiers')}</SidebarLink>
				<SidebarLink to="/travauxPonctuels" icon={<FaTools />}>{t('Travaux ponctuels')}</SidebarLink>
				<hr />
				<SidebarLink to="/offres" icon={<FaFileContract />}>{t('Offres')}</SidebarLink>
				<SidebarLink to="/offreMateriel" icon={<FaFileContract />}>{t('Offres de matériel')}</SidebarLink>
				<SidebarLink to="/contacts" icon={<FaAddressBook />}>{t('Contacts')}</SidebarLink>
				<hr />
				<SidebarLink to="/factures" icon={<FaFileInvoiceDollar />}>{t('Factures de vente')}</SidebarLink>
				<SidebarLink to="/facturesAchat" icon={<FaFileInvoiceDollar />}>{t("Factures sous-traitant")}</SidebarLink>
				<SidebarLink to="/notesDeCredit" icon={<FaFileInvoiceDollar />}>{t('Notes de crédit')}</SidebarLink>
				<hr />
				<SidebarLink to="/fournisseurs" icon={<FaShippingFast />}>{t('Fournisseurs')}</SidebarLink>
				<SidebarLink to="/personnel" icon={<FaUsers />}>{t('Personnel')}</SidebarLink>
				<SidebarLink to="/articles" icon={<FaBox />}>{t('Articles')}</SidebarLink>
				<SidebarLink to="/chantiersConcurrents" icon={<FaBuilding />}>{t('Non-concurrents')}</SidebarLink>
				{token?.scope === 'admin' && <>
					<hr />
					<SidebarCollapsible text={t('Meta')} icon={<FaCog />}>
						<SidebarLink to="/codesSortiePoubelles">{t('Codes poubelles')}</SidebarLink>
						<SidebarLink to="/typeDePlaintes">{t('Types de plaintes')}</SidebarLink>
						<SidebarLink to="/verreFrequence">{t('Fréquence de verre')}</SidebarLink>
						<SidebarLink to="/frequences">{t('Fréquences de chantiers')}</SidebarLink>
						<SidebarLink to="/offreModele">{t('Modèles d\'offres')}</SidebarLink>
						<SidebarLink to="/offreFrequences">{t('Fréquences d\'offres')}</SidebarLink>
						<SidebarLink to="/offreSections">{t('Sections d\'offres')}</SidebarLink>
						<SidebarLink to="/offreTaches">{t('Tâches d\'offres')}</SidebarLink>
					</SidebarCollapsible>
					<SidebarLink to="/societes" icon={<FaBriefcase />}>{t('Sociétés')}</SidebarLink>
					<SidebarLink to="/users" icon={<FaUser />}>{t('Users')}</SidebarLink>
				</>}
			</div>
		</Sidebar>
		<div className="grow flex flex-col h-full relative overflow-auto">
			<div className="max-w-[100vw] sticky shrink-0 overflow-auto top-0 left-0 right-0 z-40 bg-white shadow px-8 py-3 flex items-center gap-8">
				<button className="cursor-pointer min-w-fit" onClick={() => setSidebar(!sidebar)}>
					<FaBars />
				</button>
				<Breadcrumb />
				<DateInput value={date} onChange={set('date')} className={`ml-auto ${date?.toLocaleDateString() !== new Date().toLocaleDateString() ? '[&_input]:bg-red-600' : ''}`} />
				<Menu activator={
					<button className="cursor-pointer flex items-center">{<img src={`/flags/${language.locale}.png`} alt={language.locale} className="min-w-[24px] w-[24px] h-[18px]" />}</button>
				}>
					{languages.filter(x => x.locale !== locale).map(x =>
						<MenuLink key={x.locale} onClick={() => setLocale(x.locale)} icon={<img src={`/flags/${x.locale}.png`} alt={x.locale} className="min-w-[24px] w-[24px] h-[18px]" />}>{t(x.name)}</MenuLink>
					)}
				</Menu>
				<Menu activator={
					<button className="rounded-full bg-gray-300 text-gray-500 p-2 cursor-pointer">
						<FaUser />
					</button>
				}>
					<div className="px-4 py-3 min-w-[300px] bg-gray-100 rounded-t-lg flex gap-3">
						<div className="rounded-full bg-gray-300 text-gray-500 p-3">
							<FaUser className="h-6 w-6" />
						</div>
						<div>
							<div className="whitespace-nowrap">{token?.email}</div>
							<div className="whitespace-nowrap text-gray-500 italic">{roleOptions.find(x => x.value === token?.scope).label}</div>
						</div>
					</div>
					<MenuLink to="/change-password" icon={<FaStarOfLife />}>{t('Change password')}</MenuLink>
					<MenuLink onClick={logout} icon={<FaSignOutAlt />}>{t('Log out')}</MenuLink>
				</Menu>
			</div>
			<div className="grow flex p-5 md:py-6 md:px-10 w-screen md:w-[unset]">{children}</div>
		</div>
	</div>
}

export function Layout({ children }) {
	const [{ token }] = useStorage()
	if (!token) return <AuthLayout>{children}</AuthLayout>

	return <AdminLayout>{children}</AdminLayout>
}